import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CommentForm } from 'components/CommentForm/CommentForm'
import { IconButton } from 'components/IconButton/IconButton'
import { useCreateTrackedChangeComment } from 'hooks/queries/trackedChangeComments/useCreateTrackedChangeComment'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useEffect, useRef, useState } from 'react'
import { useDocStore } from 'stores/DocStore'
import { ReactComponent as PostIcon } from 'assets/icons/post16.svg'
import { ReactComponent as CancelIcon } from 'assets/icons/close16.svg'
import Styles from './NewReplyForm.module.scss'
import { MaybeNewReplyFormProps, DefinitelyNewReplyFormProps } from './Types'
import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { useKeyboard } from '@react-aria/interactions'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import { Toolbar } from 'common/ProseInput/components/Toolbar'

const DefinitelyNewReplyForm = ({ item, inputRef, articleId, layerId }: DefinitelyNewReplyFormProps) => {
  const [comment, setComment] = useState('')
  const { isReplying, setIsReplying } = useCollabItemContext()
  const isRichText = useFlipperFlag('richtext_comments')
  const { mutate: create } = useCreateTrackedChangeComment({
    articleKey: articleId,
    layerId,
    trackedChangeId: item.uuid,
  })

  const reset = () => {
    setComment('')
    inputRef?.current?.clear?.()
    setIsReplying(false)
  }

  const handleChange = (newComment: string) => {
    setIsReplying(newComment !== '')
    setComment(newComment)
  }

  const handleSubmit = async () => {
    create(
      { body: comment, html_body: isRichText ? comment : null },
      {
        onSuccess: reset,
        onError: (error) => {
          showToastMessage(error.message, 'error')
        },
      },
    )
  }

  /* -------------------------------------------------------------------------- */
  /*                                  Keyboard                                  */
  /* -------------------------------------------------------------------------- */
  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === 'Escape') {
        reset()
      }
    },
  })

  /* -------------------------------------------------------------------------- */
  /*                                 Autoscroll                                 */
  /* -------------------------------------------------------------------------- */
  const ref = useRef<HTMLDivElement | null>(null)
  const onType = useLocalDebouncedFn(
    () => {
      ref.current?.scrollIntoView?.({ block: 'nearest' })
    },
    200,
    {},
    [],
  )
  useEffect(() => {
    if (isReplying) {
      onType()
    }
    return () => onType.cancel()
  }, [comment, isReplying, onType])

  return (
    <div {...keyboardProps} ref={ref}>
      <CommentForm
        inputRef={inputRef}
        value={comment}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={reset}
        className={Styles.form}
        inputContainerClassName={Styles.inputContainer}
        inputClassName={Styles.input}
        primaryCta={<IconButton variant="primary" title="Post" icon={PostIcon} />}
        secondaryCta={<IconButton variant="tertiary" title="Cancel" icon={CancelIcon} />}
        hideActionsOnEmpty
        isHtmlComment={isRichText}
        footer={
          isRichText && (
            <Toolbar.Root>
              <Toolbar.Bold />
              <Toolbar.BulletList />
              <Toolbar.Mention />
            </Toolbar.Root>
          )
        }
      />
    </div>
  )
}

const MaybeNewReplyForm = ({ item, inputRef }: MaybeNewReplyFormProps) => {
  const docStore = useDocStore()
  if (!docStore.layerId) {
    return null
  }

  return (
    <DefinitelyNewReplyForm inputRef={inputRef} item={item} articleId={docStore.articleId} layerId={docStore.layerId} />
  )
}

export { MaybeNewReplyForm }
